import React from 'react';

// The server's administration parameters for this instrument. Fallbacks only —
// only the seed varies between runs, so a client default must never be
// substituted for a value the server actually sent. windowMs must be a NUMBER:
// the runner derives its reaction-time ceiling as `timing.windowMs + 250`, so a
// string window would concatenate ("2000" + 250) and lift the ceiling to
// 2,000,250ms, accepting reaction times that are plainly impossible.
const DEFAULT_WINDOW_MS = 2000;
const DEFAULT_ISI_MS = 1200;
const FIXATION_MS = 400;

// One unbroken row, evenly spaced, in a fixed-advance face. The flanking
// manipulation depends on the centre arrow being crowded by its neighbours, so
// every character is drawn identically: no emphasis, no per-character element,
// no extra spacing around the middle. A visually distinct centre would let the
// participant find it instead of resisting the flankers, which is the whole
// measurement.
function StimulusView({ stimulus, compact }) {
  return (
    <span
      // The characters are punctuation and are announced inconsistently, if at
      // all. A label naming the directions would speak the answer aloud, so the
      // row is labelled as an object only.
      role="img"
      aria-label="arrow row"
      style={{
        fontSize: compact ? '22px' : 'clamp(48px, 13vw, 110px)',
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: 'var(--text-primary)',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        whiteSpace: 'pre',
      }}
    >
      {stimulus.array}
    </span>
  );
}

export const flankerTask = {
  id: 'flanker',
  name: 'Arrow Rush',
  pillar: 'attention',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'Five arrows will appear in a row.',
    'Report the direction of the MIDDLE arrow only.',
    'Ignore the four arrows around it.',
  ],
  targets: [
    { id: 'left', label: 'Left', keys: ['ArrowLeft', 'f'], hint: '←' },
    { id: 'right', label: 'Right', keys: ['ArrowRight', 'j'], hint: '→' },
  ],

  examples: [
    { stimulus: { array: '>><>>' }, response: 'Left', note: 'Middle arrow wins' },
    { stimulus: { array: '<<<<<' }, response: 'Left' },
  ],

  toAnswer: (targetId) => ({ direction: targetId }),

  // Deliberately ignores the per-trial second argument. The score is the
  // contrast between the two kinds of trial the server interleaves, and the
  // client is never told which trial is which; pacing that varied with the
  // stimulus would both leak that classification and change what the contrast
  // measures.
  timing: (params) => {
    const isiMs = Number(params?.isi_ms) || DEFAULT_ISI_MS;
    return {
      fixationMs: FIXATION_MS,
      windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
      // The fixation cross is drawn inside the served inter-stimulus interval,
      // not on top of it, so it comes out of the gap: fixation + gap === isi_ms.
      //
      // That is this module's arithmetic, and it is still exactly what these two
      // numbers say. It is no longer the whole of the REALIZED interval: this
      // task does not hold its full window, so an answered trial ends 250ms
      // after the response and its stimulus-offset-to-next-onset is
      // `isi_ms + 250`, while an unanswered one still ends on the window. The
      // post-response interval is now constant instead of varying with reaction
      // time, which is the better property — but do not read the line above as
      // describing the interval between stimuli on an answered trial.
      gapMs: Math.max(0, isiMs - FIXATION_MS),
    };
  },

  // PRACTICE ONLY — the runner gates this on trial.isPractice, and the
  // assessment driver never feeds back on a scored trial. Nothing hidden is
  // used: the centre character is on screen and reading it is the whole rule.
  // The centre is derived from the served row, not pinned to index 2. The row is
  // five arrows today, but pattern-comparison derives its geometry from served
  // `gridPoints` for exactly this reason, and a seven-arrow row would otherwise
  // be graded against the wrong arrow with no other symptom.
  //
  // Returns null rather than guessing on a row that is missing, empty, or of
  // even length (which has no centre). The previous `=== '<' ? 'left' : 'right'`
  // resolved undefined to 'right', so a malformed trial was fed back
  // confidently, and a non-response on one was scored as wrong instead of as
  // having no expectation at all.
  practiceExpectation: (stimulus) => {
    const row = stimulus?.array;
    if (typeof row !== 'string' || row.length === 0 || row.length % 2 === 0) return null;
    const centre = row[(row.length - 1) / 2];
    if (centre !== '<' && centre !== '>') return null;
    return centre === '<' ? 'left' : 'right';
  },

  StimulusView,
};
